<script setup>
import ObjectiveTimesConfigurationAction from '@frontJs/components/actions/projectManagement/tasks/ObjectiveTimesConfigurationAction.vue';
import ObjectiveCounterActionCell from '@frontJs/components/tables/cells/projectManagement/tasks/ObjectiveCounterActionCell.vue';
import ObjectiveToggleActionCell from '@frontJs/components/tables/cells/projectManagement/tasks/ObjectiveToggleActionCell.vue';
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import FileTextIcon from '@iconify-vue/ph/file-text';
import GearIcon from '@iconify-vue/ph/gear-fill';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import GaugeCell from '@js/components/tables/cells/GaugeCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import RatingCell from '@js/components/tables/cells/RatingCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { overview, seo, sea, sma, smo, sem, cs } = defineProps({
    overview: {
        type: Boolean,
        default: false,
    },
    seo: {
        type: Boolean,
        default: false,
    },
    sea: {
        type: Boolean,
        default: false,
    },
    sma: {
        type: Boolean,
        default: false,
    },
    smo: {
        type: Boolean,
        default: false,
    },
    sem: {
        type: Boolean,
        default: false,
    },
    cs: {
        type: Boolean,
        default: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const renderDetailRow = (label, time, price, emphasize = true) =>
    h(
        'div',
        {
            class: 'flex items-center justify-between gap-4',
        },
        [
            h(
                'span',
                emphasize
                    ? {
                          class: 'inline-flex w-fit items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700',
                      }
                    : {
                          class: 'text-sm text-gray-600',
                      },
                label,
            ),
            h(
                'div',
                {
                    class: 'flex items-center gap-2 whitespace-nowrap',
                },
                [
                    price
                        ? h(
                              'span',
                              {
                                  class: 'text-xs font-normal text-gray-500',
                              },
                              price,
                          )
                        : null,
                    time
                        ? h(
                              'span',
                              {
                                  class: emphasize
                                      ? 'text-lg font-bold'
                                      : 'text-sm font-semibold',
                              },
                              time,
                          )
                        : null,
                ],
            ),
        ],
    );

const renderFlatDetails = (details) =>
    h(
        'ul',
        { class: 'flex flex-col gap-4' },
        details.map((detail) =>
            h(
                'li',
                {
                    class: 'rounded-lg border border-gray-200 bg-white p-4',
                },
                renderDetailRow(
                    detail.label,
                    detail.formattedTime,
                    detail.formattedPrice,
                ),
            ),
        ),
    );

const renderGroupedDetails = (groups) =>
    h(
        'ul',
        { class: 'flex flex-col gap-4' },
        groups.map((group) =>
            h(
                'li',
                {
                    class: 'flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4',
                },
                [
                    renderDetailRow(
                        group.label,
                        group.formattedTime,
                        group.formattedPrice,
                    ),
                    (group.items ?? []).length > 0
                        ? h(
                              'ul',
                              {
                                  class: 'flex flex-col gap-2 border-t border-gray-100 pt-3',
                              },
                              group.items.map((item) =>
                                  h(
                                      'li',
                                      {},
                                      renderDetailRow(
                                          item.label,
                                          item.formattedTime,
                                          item.formattedPrice,
                                          false,
                                      ),
                                  ),
                              ),
                          )
                        : null,
                ],
            ),
        ),
    );

const renderPackageInfo = (info) =>
    h(
        'div',
        {
            class: 'flex flex-col gap-2 rounded-lg border border-indigo-100 bg-white p-4',
        },
        info.map((item) =>
            h(
                'div',
                {
                    class: 'flex items-center justify-between gap-4',
                },
                [
                    h('span', { class: 'text-sm text-gray-600' }, item.label),
                    h(
                        'span',
                        { class: 'text-sm font-semibold whitespace-nowrap' },
                        item.value,
                    ),
                ],
            ),
        ),
    );

const renderGaugeFooter = (rows) =>
    h(
        'div',
        { class: 'flex flex-col gap-0.5 text-xs text-gray-500' },
        rows
            .filter((row) => row.value)
            .map((row) =>
                h('div', { class: 'flex items-center justify-between gap-2' }, [
                    h('span', {}, row.label),
                    h(
                        'span',
                        {
                            class: 'font-semibold whitespace-nowrap text-gray-700',
                        },
                        row.value,
                    ),
                ]),
            ),
    );

const renderDetailsAction = ({ modalTitle, details, packageInfo, grouped }) =>
    h(
        DisplayContentAction,
        {
            title: modalTitle,
        },
        {
            trigger: () =>
                h(ActionButton, {
                    size: 'small',
                    title: 'Voir le détail',
                    type: 'primary',
                    square: true,
                    icon: FileTextIcon,
                }),
            complexContent: () => {
                const detailsContent = grouped
                    ? renderGroupedDetails(details)
                    : renderFlatDetails(details);

                return packageInfo
                    ? h('div', { class: 'flex flex-col gap-4' }, [
                          renderPackageInfo(packageInfo),
                          detailsContent,
                      ])
                    : detailsContent;
            },
        },
    );

const detailsColumn = ({
    id,
    title,
    modalTitle,
    totalTimeField,
    formattedTimeField,
    detailsField,
    packageInfoField = null,
    grouped = false,
}) =>
    columnHelper.accessor(() => null, {
        id,
        headerCustom: true,
        header: (props) =>
            h(HeaderCell, {
                header: props.header,
                title,
                width: 0,
            }),
        cell: (props) => {
            const details = props.row.original[detailsField] ?? [];

            return props.row.original[totalTimeField] || details.length > 0
                ? h(
                      TextCell,
                      {
                          text: props.row.original[formattedTimeField],
                      },
                      {
                          actions: () =>
                              details.length > 0
                                  ? renderDetailsAction({
                                        modalTitle,
                                        details,
                                        packageInfo: packageInfoField
                                            ? (props.row.original[
                                                  packageInfoField
                                              ] ?? null)
                                            : null,
                                        grouped,
                                    })
                                  : null,
                      },
                  )
                : null;
        },
        enableSorting: false,
    });

/**
 * Merges the time details and the achievement gauge of a theme into a single column.
 */
const recapColumn = ({
    id,
    title,
    modalTitle,
    totalTimeField,
    formattedTimeField,
    detailsField,
    percentageField,
    packageInfoField = null,
    gaugeRows = () => [],
    grouped = false,
}) =>
    columnHelper.accessor(() => null, {
        id,
        headerCustom: true,
        header: (props) =>
            h(HeaderCell, {
                header: props.header,
                title,
                width: 0,
            }),
        cell: (props) => {
            const row = props.row.original;
            const details = row[detailsField] ?? [];
            const hasTime = Boolean(row[totalTimeField]) || details.length > 0;
            const percentage = row[percentageField];

            if (!hasTime && percentage === null) {
                return null;
            }

            const timeRow = hasTime
                ? h(
                      'div',
                      { class: 'flex items-center justify-between gap-2' },
                      [
                          h(
                              'span',
                              { class: 'text-xs text-gray-500' },
                              'Temps',
                          ),
                          h('div', { class: 'flex items-center gap-1' }, [
                              h(
                                  'span',
                                  {
                                      class: 'text-sm font-bold whitespace-nowrap',
                                  },
                                  row[formattedTimeField],
                              ),
                              details.length > 0
                                  ? renderDetailsAction({
                                        modalTitle,
                                        details,
                                        packageInfo: packageInfoField
                                            ? (row[packageInfoField] ?? null)
                                            : null,
                                        grouped,
                                    })
                                  : null,
                          ]),
                      ],
                  )
                : null;

            if (percentage === null) {
                return h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                'div',
                                { class: 'flex w-40 flex-col gap-1' },
                                timeRow,
                            ),
                    },
                );
            }

            return h(
                GaugeCell,
                {
                    percentage,
                    width: '160px',
                },
                {
                    footer: () =>
                        h('div', { class: 'flex flex-col gap-0.5' }, [
                            timeRow,
                            renderGaugeFooter(gaugeRows(row)),
                        ]),
                },
            );
        },
        enableSorting: false,
    });

const notesColumn = ({ id, title, subTab }) =>
    columnHelper.display({
        id,
        headerCustom: true,
        header: (props) =>
            h(HeaderCell, {
                header: props.header,
                title,
                width: 0,
            }),
        cell: (props) =>
            h(
                BaseCell,
                {},
                {
                    default: () =>
                        h(ActionButton, {
                            size: 'small',
                            square: true,
                            title,
                            href: route(
                                'front.projectManagement.customer.updateCustomer',
                                {
                                    tenant: props.row.original.id,
                                    tab: 'notes',
                                    subTab,
                                },
                            ),
                            target: '_blank',
                            icon: NotePencilIcon,
                            type: 'secondary',
                        }),
                },
            ),
        enableSorting: false,
    });

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href:
                        route(
                            'front.projectManagement.customer.updateCustomer',
                            {
                                tenant: props.row.original.id,
                            },
                        ) + '?tab=apikomObjectives',
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Rapport',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Prévisualiser le rapport',
                                callback: () => {
                                    const params = new URLSearchParams({
                                        token: props.row.original
                                            .superAdminLoginToken,
                                        redirect: 'app.apikom.report',
                                        'redirectParams[tab]':
                                            'apikomMonthlyServices',
                                        'redirectParams[preview]': '1',
                                    });

                                    window.open(
                                        '/' +
                                            props.row.original.id +
                                            '/login?' +
                                            params.toString(),
                                        'reportPreview',
                                        'popup,width=1280,height=900,scrollbars=yes,resizable=yes',
                                    );
                                },
                                icon: ArrowSquareOutIcon,
                                type: 'secondary',
                            }),
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seoGeoSummary',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEO/GEO',
                }),
            cell: (props) =>
                props.row.original.seoGeoTotal > 0
                    ? h(RatingCell, {
                          rating: props.row.original.seoGeoDone,
                          total: props.row.original.seoGeoTotal,
                          color:
                              props.row.original.seoGeoDone >=
                              props.row.original.seoGeoTotal
                                  ? 'green'
                                  : 'red',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfStandardContentsPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Contenus<br/>standard',
                }),
            cell: (props) =>
                props.row.original.numberOfStandardContentsPerMonth
                    ? h(RatingCell, {
                          rating: props.row.original
                              .numberOfStandardContentsPerMonthDone,
                          total: props.row.original
                              .numberOfStandardContentsPerMonth,
                          color:
                              props.row.original
                                  .numberOfStandardContentsPerMonthDone >=
                              props.row.original
                                  .numberOfStandardContentsPerMonth
                                  ? 'green'
                                  : 'red',
                          href: route(
                              'front.projectManagement.seo.apikomContents',
                              { tenant: props.row.original.id },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfPremiumContentsPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Contenus<br/>premium',
                }),
            cell: (props) =>
                props.row.original.numberOfPremiumContentsPerMonth
                    ? h(RatingCell, {
                          rating: props.row.original
                              .numberOfPremiumContentsPerMonthDone,
                          total: props.row.original
                              .numberOfPremiumContentsPerMonth,
                          color:
                              props.row.original
                                  .numberOfPremiumContentsPerMonthDone >=
                              props.row.original.numberOfPremiumContentsPerMonth
                                  ? 'green'
                                  : 'red',
                          href: route(
                              'front.projectManagement.seo.apikomContents',
                              { tenant: props.row.original.id },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfMetasPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Meta',
                }),
            cell: (props) =>
                props.row.original.numberOfMetasPerMonth
                    ? h(RatingCell, {
                          rating: props.row.original.numberOfMetasPerMonthDone,
                          total: props.row.original.numberOfMetasPerMonth,
                          color:
                              props.row.original.numberOfMetasPerMonthDone >=
                              props.row.original.numberOfMetasPerMonth
                                  ? 'green'
                                  : 'red',
                          href: route(
                              'front.projectManagement.seo.apikomContents',
                              { tenant: props.row.original.id },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfNetlinkingPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Netlinking',
                }),
            cell: (props) =>
                props.row.original.hasNetlinking
                    ? h(NumberCell, {
                          number: props.row.original
                              .numberOfNetlinkingPerMonthDone,
                          color: props.row.original
                              .numberOfNetlinkingPerMonthDone
                              ? 'green'
                              : 'red',
                          href: route(
                              'front.projectManagement.seo.netlinking',
                              { tenant: props.row.original.id },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfGoogleSearchConsolePerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse<br/>Search Console',
                }),
            cell: (props) =>
                props.row.original.numberOfGoogleSearchConsolePerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'google_search_console',
                          achievedAt:
                              props.row.original.googleSearchConsoleAchievedAt,
                          lastAchievedAt:
                              props.row.original.googleSearchConsoleLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfTechnicalAuditPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse<br/>technique',
                }),
            cell: (props) =>
                props.row.original.numberOfTechnicalAuditPerMonth
                    ? h(ObjectiveCounterActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'technical_audit',
                          minutes: props.row.original.technicalAuditMinutes,
                          canEdit: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seoRecommendationCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Recos<br/>SEO',
                }),
            cell: (props) =>
                props.row.original.seoRecommendationCount > 0
                    ? h(NumberCell, {
                          number: props.row.original.seoRecommendationCount,
                          color: 'green',
                          href: route('front.projectManagement.task.tickets', {
                              tenant: props.row.original.id,
                          }),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'devSeoCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'DEV SEO',
                }),
            cell: (props) =>
                props.row.original.devSeoCount > 0
                    ? h(NumberCell, {
                          number: props.row.original.devSeoCount,
                          color: 'green',
                          href: route('front.projectManagement.seo.devSeo'),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfGoogleMyBusinessPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Google<br/>My Business',
                }),
            cell: (props) =>
                props.row.original.numberOfGoogleMyBusinessPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'google_my_business',
                          achievedAt:
                              props.row.original.googleMyBusinessAchievedAt,
                          lastAchievedAt:
                              props.row.original.googleMyBusinessLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seoAiAnalysis',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse IA',
                }),
            cell: (props) =>
                props.row.original.numberOfSeoAiAnalysisPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'seo_ai_analysis',
                          achievedAt:
                              props.row.original.seoAiAnalysisAchievedAt,
                          lastAchievedAt:
                              props.row.original.seoAiAnalysisLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        notesColumn({
            id: 'seoNotes',
            title: 'Notes',
            subTab: 'seoNotes',
        }),
        columnHelper.accessor(() => null, {
            id: 'seaSummary',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEA',
                }),
            cell: (props) =>
                props.row.original.seaTotal > 0
                    ? h(RatingCell, {
                          rating: props.row.original.seaDone,
                          total: props.row.original.seaTotal,
                          color:
                              props.row.original.seaDone >=
                              props.row.original.seaTotal
                                  ? 'green'
                                  : 'red',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smaSummary',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMA',
                }),
            cell: (props) =>
                props.row.original.smaTotal > 0
                    ? h(RatingCell, {
                          rating: props.row.original.smaDone,
                          total: props.row.original.smaTotal,
                          color:
                              props.row.original.smaDone >=
                              props.row.original.smaTotal
                                  ? 'green'
                                  : 'red',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seaCampaignOptimization',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse/optimisation<br/>campagnes',
                }),
            cell: (props) =>
                props.row.original.numberOfSeaCampaignOptimizationPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sea_campaign_optimization',
                          achievedAt:
                              props.row.original.seaCampaignOptimizationAchievedAt,
                          lastAchievedAt:
                              props.row.original.seaCampaignOptimizationLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seaGoogleAdsQuality',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Veille/optimisation<br/>qualité Google Ads',
                }),
            cell: (props) =>
                props.row.original.numberOfSeaGoogleAdsQualityPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sea_google_ads_quality',
                          achievedAt:
                              props.row.original.seaGoogleAdsQualityAchievedAt,
                          lastAchievedAt:
                              props.row.original.seaGoogleAdsQualityLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seaMerchantCenterHealth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Santé compte<br/>Merchant Center',
                }),
            cell: (props) =>
                props.row.original.numberOfSeaMerchantCenterHealthPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sea_merchant_center_health',
                          achievedAt:
                              props.row.original.seaMerchantCenterHealthAchievedAt,
                          lastAchievedAt:
                              props.row.original.seaMerchantCenterHealthLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seaAiAnalysis',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse IA',
                }),
            cell: (props) =>
                props.row.original.numberOfSeaAiAnalysisPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sea_ai_analysis',
                          achievedAt:
                              props.row.original.seaAiAnalysisAchievedAt,
                          lastAchievedAt:
                              props.row.original.seaAiAnalysisLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        notesColumn({
            id: 'seaNotes',
            title: 'Notes',
            subTab: 'seaNotes',
        }),
        recapColumn({
            id: 'seaRecap',
            title: 'Récap',
            modalTitle: 'Détails du temps SEA',
            totalTimeField: 'seaTotalTime',
            formattedTimeField: 'formattedSeaTotalTime',
            detailsField: 'seaDetails',
            percentageField: 'seaAchievementPercentage',
            packageInfoField: 'seaPackageInfo',
            gaugeRows: (row) => [
                {
                    label: 'Forfait SEA',
                    value: row.seaPackageBudgetTime,
                },
                {
                    label: 'Dépenses M-1',
                    value: row.seaCost,
                },
            ],
        }),
        columnHelper.accessor(() => null, {
            id: 'smaCampaignOptimization',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse/optimisation<br/>campagnes',
                }),
            cell: (props) =>
                props.row.original.numberOfSmaCampaignOptimizationPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sma_campaign_optimization',
                          achievedAt:
                              props.row.original.smaCampaignOptimizationAchievedAt,
                          lastAchievedAt:
                              props.row.original.smaCampaignOptimizationLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smaAccountQuality',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Qualité compte<br/>et suivi technique',
                }),
            cell: (props) =>
                props.row.original.numberOfSmaAccountQualityPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sma_account_quality',
                          achievedAt:
                              props.row.original.smaAccountQualityAchievedAt,
                          lastAchievedAt:
                              props.row.original.smaAccountQualityLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smaShoppingFeedHealth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Santé flux<br/>shopping Meta',
                }),
            cell: (props) =>
                props.row.original.numberOfSmaShoppingFeedHealthPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sma_shopping_feed_health',
                          achievedAt:
                              props.row.original.smaShoppingFeedHealthAchievedAt,
                          lastAchievedAt:
                              props.row.original.smaShoppingFeedHealthLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smaAiAnalysis',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse IA',
                }),
            cell: (props) =>
                props.row.original.numberOfSmaAiAnalysisPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'sma_ai_analysis',
                          achievedAt:
                              props.row.original.smaAiAnalysisAchievedAt,
                          lastAchievedAt:
                              props.row.original.smaAiAnalysisLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        notesColumn({
            id: 'smaNotes',
            title: 'Notes',
            subTab: 'smaNotes',
        }),
        recapColumn({
            id: 'smaRecap',
            title: 'Récap',
            modalTitle: 'Détails du temps SMA',
            totalTimeField: 'smaTotalTime',
            formattedTimeField: 'formattedSmaTotalTime',
            detailsField: 'smaDetails',
            percentageField: 'smaAchievementPercentage',
            packageInfoField: 'smaPackageInfo',
            gaugeRows: (row) => [
                {
                    label: 'Forfait SMA',
                    value: row.smaPackageBudgetTime,
                },
                {
                    label: 'Dépenses M-1',
                    value: row.smaCost,
                },
            ],
        }),
        columnHelper.accessor(() => null, {
            id: 'smoSummary',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMO',
                }),
            cell: (props) =>
                props.row.original.smoTotal > 0
                    ? h(RatingCell, {
                          rating: props.row.original.smoDone,
                          total: props.row.original.smoTotal,
                          color:
                              props.row.original.smoDone >=
                              props.row.original.smoTotal
                                  ? 'green'
                                  : 'red',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEditorialFrameworkPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Trame<br/>éditoriale',
                }),
            cell: (props) =>
                props.row.original.numberOfEditorialFrameworkPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'editorial_framework',
                          achievedAt:
                              props.row.original.editorialFrameworkAchievedAt,
                          lastAchievedAt:
                              props.row.original.editorialFrameworkLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'smoAiAnalysis',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Analyse IA',
                }),
            cell: (props) =>
                props.row.original.numberOfSmoAiAnalysisPerMonth
                    ? h(ObjectiveToggleActionCell, {
                          tenant: props.row.original,
                          objectiveName: 'smo_ai_analysis',
                          achievedAt:
                              props.row.original.smoAiAnalysisAchievedAt,
                          lastAchievedAt:
                              props.row.original.smoAiAnalysisLastAchievedAt,
                          canBeToggle: props.row.original.isCurrentMonth,
                          callback: () => updateData(),
                      })
                    : null,
            enableSorting: false,
        }),
        notesColumn({
            id: 'smoNotes',
            title: 'Notes',
            subTab: 'smoNotes',
        }),
        recapColumn({
            id: 'smoRecap',
            title: 'Récap',
            modalTitle: 'Détails du temps SMO',
            totalTimeField: 'smoTotalTime',
            formattedTimeField: 'formattedSmoTotalTime',
            detailsField: 'smoDetails',
            percentageField: 'smoAchievementPercentage',
            packageInfoField: 'smoPackageInfo',
            gaugeRows: (row) => [
                {
                    label: 'Forfait SMO',
                    value: row.smoPackageBudgetTime,
                },
            ],
        }),
        columnHelper.accessor(() => null, {
            id: 'semSummary',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEM',
                }),
            cell: (props) =>
                props.row.original.semTotal > 0
                    ? h(RatingCell, {
                          rating: props.row.original.semDone,
                          total: props.row.original.semTotal,
                          color:
                              props.row.original.semDone >=
                              props.row.original.semTotal
                                  ? 'green'
                                  : 'red',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'invoicedTime',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'DEV facturé',
                }),
            cell: (props) =>
                props.row.original.invoicedTime
                    ? h(TextCell, {
                          text: props.row.original.formattedInvoicedTime,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'csInvoicedTime',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CS facturé',
                }),
            cell: (props) =>
                props.row.original.csInvoicedTime
                    ? h(TextCell, {
                          text: props.row.original.formattedCsInvoicedTime,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'roadmap',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Feuille<br/>de route',
                }),
            cell: (props) =>
                h(ObjectiveCounterActionCell, {
                    tenant: props.row.original,
                    objectiveName: 'roadmap',
                    minutes: props.row.original.roadmapMinutes,
                    canEdit: props.row.original.isCurrentMonth,
                    href: route(
                        'front.projectManagement.customer.updateCustomer',
                        {
                            tenant: props.row.original.id,
                            tab: 'roadmap',
                        },
                    ),
                    callback: () => updateData(),
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'csRecommendationCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Recommandations<br/>CS',
                }),
            cell: (props) =>
                props.row.original.csRecommendationCount > 0
                    ? h(NumberCell, {
                          number: props.row.original.csRecommendationCount,
                          color: 'green',
                          href: route('front.projectManagement.task.tickets', {
                              tenant: props.row.original.id,
                          }),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'appointmentCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'RDV',
                }),
            cell: (props) =>
                props.row.original.appointmentCount > 0
                    ? h(NumberCell, {
                          number: props.row.original.appointmentCount,
                          color: 'green',
                          href: route(
                              'front.projectManagement.apikom.appointments',
                              { tenant: props.row.original.id },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        detailsColumn({
            id: 'csDetails',
            title: 'Détails',
            modalTitle: 'Détails du temps CS',
            totalTimeField: 'csTotalTime',
            formattedTimeField: 'formattedCsTotalTime',
            detailsField: 'csDetails',
        }),
        recapColumn({
            id: 'globalRecap',
            title: 'Récap',
            modalTitle: 'Détails du temps global',
            totalTimeField: 'globalTotalTime',
            formattedTimeField: 'formattedGlobalTotalTime',
            detailsField: 'globalDetails',
            percentageField: 'globalAchievementPercentage',
            packageInfoField: 'daysPackageInfo',
            grouped: true,
            gaugeRows: (row) => [
                {
                    label: 'Forfait jours',
                    value: row.globalPackageBudgetTime,
                },
            ],
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEmailingPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Emailings',
                }),
            cell: (props) =>
                props.row.original.numberOfEmailingPerMonth
                    ? h(RatingCell, {
                          rating: props.row.original
                              .numberOfEmailingPerMonthDone,
                          total: props.row.original.numberOfEmailingPerMonth,
                          color:
                              props.row.original.numberOfEmailingPerMonthDone >=
                              props.row.original.numberOfEmailingPerMonth
                                  ? 'green'
                                  : 'red',
                          href: route(
                              'front.projectManagement.modules.emailings',
                              {
                                  tenant: props.row.original.id,
                              },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'emailingAutomationCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Emailings<br/>automatisés',
                }),
            cell: (props) =>
                props.row.original.emailingAutomationCount > 0
                    ? h(NumberCell, {
                          number: props.row.original.emailingAutomationCount,
                          color: 'green',
                          href: route(
                              'front.projectManagement.modules.emailingAutomations',
                              {
                                  tenant: props.row.original.id,
                              },
                          ),
                          inertia: true,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfShortMessageServicesPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMS',
                }),
            cell: (props) =>
                props.row.original.numberOfShortMessageServicesPerMonth
                    ? h(RatingCell, {
                          rating: props.row.original
                              .numberOfShortMessageServicesPerMonthDone,
                          total: props.row.original
                              .numberOfShortMessageServicesPerMonth,
                          color:
                              props.row.original
                                  .numberOfShortMessageServicesPerMonthDone >=
                              props.row.original
                                  .numberOfShortMessageServicesPerMonth
                                  ? 'green'
                                  : 'red',
                          href: route(
                              'front.projectManagement.sem.shortMessageServices',
                              {
                                  tenant: props.row.original.id,
                              },
                          ),
                      })
                    : null,
            enableSorting: false,
        }),
        recapColumn({
            id: 'seoRecap',
            title: 'Récap',
            modalTitle: 'Détails du temps SEO',
            totalTimeField: 'seoTotalTime',
            formattedTimeField: 'formattedSeoTotalTime',
            detailsField: 'seoDetails',
            percentageField: 'seoAchievementPercentage',
            packageInfoField: 'seoPackageInfo',
            gaugeRows: (row) => [
                {
                    label: 'Forfait SEO/GEO',
                    value: row.seoPackageBudgetTime,
                },
                {
                    label: 'Netlinking',
                    value: row.seoNetlinkingBudget,
                },
            ],
        }),
    ],
    url: route('front.projectManagement.task.objectives'),
    data: {
        overview,
        seo,
        sea,
        sma,
        smo,
        sem,
        cs,
    },
    columnVisibility: {
        seoGeoSummary: overview,
        smoSummary: overview,
        semSummary: overview,
        numberOfStandardContentsPerMonth: seo,
        numberOfPremiumContentsPerMonth: seo,
        numberOfMetasPerMonth: seo,
        numberOfNetlinkingPerMonth: seo,
        numberOfTechnicalAuditPerMonth: seo,
        numberOfGoogleSearchConsolePerMonth: seo,
        numberOfGoogleMyBusinessPerMonth: seo,
        seoRecommendationCount: seo,
        devSeoCount: seo,
        seoAiAnalysis: seo,
        seoNotes: seo,
        seoRecap: seo,
        seaSummary: overview,
        smaSummary: overview,
        seaCampaignOptimization: sea,
        seaGoogleAdsQuality: sea,
        seaMerchantCenterHealth: sea,
        seaAiAnalysis: sea,
        seaNotes: sea,
        seaRecap: sea,
        smaCampaignOptimization: sma,
        smaAccountQuality: sma,
        smaShoppingFeedHealth: sma,
        smaAiAnalysis: sma,
        smaNotes: sma,
        smaRecap: sma,
        numberOfEditorialFrameworkPerMonth: smo,
        smoAiAnalysis: smo,
        smoNotes: smo,
        smoRecap: smo,
        numberOfEmailingPerMonth: sem,
        numberOfShortMessageServicesPerMonth: sem,
        emailingAutomationCount: sem,
        invoicedTime: overview,
        csInvoicedTime: overview,
        globalRecap: overview,
        roadmap: cs,
        csRecommendationCount: cs,
        appointmentCount: cs,
        csDetails: cs,
    },
    columnPinning: {
        right: [
            'seoNotes',
            'seaNotes',
            'smoNotes',
            'smaNotes',
            'actions',
            'globalRecap',
            'csDetails',
            'seoRecap',
            'smoRecap',
            'smaRecap',
            'seaRecap',
        ],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ObjectiveTimesConfigurationAction :callback="updateData">
                <template #trigger>
                    <ActionButton
                        :left-icon="GearIcon"
                        size="small"
                        type="primary"
                    >
                        Configurer le temps
                    </ActionButton>
                </template>
            </ObjectiveTimesConfigurationAction>
        </template>
    </DataTableLayout>
</template>
